// =====================================================================
//  Shared finance formulas — Malaysian banking conventions
//  Verified test values at the bottom (run window.Calc.finance._verify())
// =====================================================================
window.Calc = window.Calc || {};
Calc.finance = {

  // ===== INTEREST/PROFIT RATE MODELS =====================================
  //
  // FLAT RATE (Malaysian convention — Personal Financing-i, HP, Personal Loan)
  //   Total profit/interest = P × R × Y           (simple, charged upfront)
  //   Total repayment       = P + total profit
  //   Monthly instalment    = total repayment / (Y × 12)
  //
  flatRate(principal, annualRate, years) {
    const months = (years || 0) * 12;
    const p = principal || 0;
    const r = annualRate || 0;
    const y = years || 0;
    const totalInterest = p * r * y;
    const totalRepayment = p + totalInterest;
    const monthly = months > 0 ? totalRepayment / months : 0;
    return {
      principal: p, annualRate: r, years: y, months,
      totalInterest, totalRepayment,
      monthlyInstalment: monthly,
      method: 'flat',
    };
  },

  // REDUCING BALANCE (Malaysian convention — Mortgage, Refinancing)
  //   PMT formula: M = P × r / (1 - (1 + r)^-n)
  //   where r = monthly nominal rate, n = total months
  //
  reducingRate(principal, annualRate, years) {
    const months = (years || 0) * 12;
    const p = principal || 0;
    const r = (annualRate || 0) / 12;
    let monthly = 0;
    if (months > 0) {
      monthly = r === 0 ? p / months : (p * r) / (1 - Math.pow(1 + r, -months));
    }
    const totalRepayment = monthly * months;
    return {
      principal: p, annualRate: annualRate || 0, years: years || 0, months,
      totalInterest: totalRepayment - p,
      totalRepayment,
      monthlyInstalment: monthly,
      method: 'reducing',
    };
  },

  // ===== RATE CONVERSIONS ================================================

  // FLAT → EFFECTIVE (APR)
  // Solve r in PMT formula:  M = P × r / (1 - (1+r)^-n)
  // Returns nominal annual rate (r_monthly × 12) — this is the BNM-disclosed APR.
  // Newton-Raphson with bisection fallback for robustness.
  solveEffectiveRate(principal, monthlyInstalment, years) {
    const n = (years || 0) * 12;
    const P = principal || 0;
    const M = monthlyInstalment || 0;
    if (n === 0 || P === 0 || M === 0) return 0;
    if (M * n <= P) return 0;   // no interest at all

    // Initial guess: rule-of-thumb 2 × flat × n / (n+1)
    const flatEquiv = (M * n - P) / (P * (n / 12));   // implicit flat rate
    let rA = (flatEquiv * 2 * n / (n + 1));           // approx APR
    let r = rA / 12;                                   // monthly

    // Newton-Raphson — converges in ~5-8 iterations
    for (let i = 0; i < 30; i++) {
      const onePlusR = 1 + r;
      const power = Math.pow(onePlusR, n);
      const denom = power - 1;
      if (denom === 0) break;
      const f = P * r * power / denom - M;
      // Derivative w.r.t. r:
      // d/dr [ P r (1+r)^n / ((1+r)^n - 1) ]
      const dpower = n * Math.pow(onePlusR, n - 1);
      const numer = P * r * power;
      const fPrime = (P * power + P * r * dpower) / denom
                   - numer * dpower / (denom * denom);
      if (fPrime === 0) break;
      const rNew = r - f / fPrime;
      if (!isFinite(rNew) || rNew <= 0) break;
      if (Math.abs(rNew - r) < 1e-12) { r = rNew; break; }
      r = rNew;
    }

    // Sanity check: if Newton-Raphson misbehaved, fall back to bisection
    let result = r * 12;
    const verify = M - (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    if (Math.abs(verify) > 0.01) {
      result = Calc.finance._bisectEffective(P, M, n);
    }
    return result;
  },

  _bisectEffective(P, M, n) {
    let lo = 0, hi = 2;   // 0% – 200% APR
    for (let i = 0; i < 80; i++) {
      const mid = (lo + hi) / 2;
      const r = mid / 12;
      const m = r === 0 ? P / n : (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      if (m > M) hi = mid; else lo = mid;
    }
    return (lo + hi) / 2;
  },

  // EFFECTIVE → FLAT
  // Given an effective (reducing) rate, find the equivalent flat rate
  // that produces the same monthly instalment.
  solveFlatEquivalent(principal, effectiveRate, years) {
    const reducing = Calc.finance.reducingRate(principal, effectiveRate, years);
    const totalInterest = reducing.totalRepayment - principal;
    return years > 0 ? totalInterest / (principal * years) : 0;
  },

  // SBR + Spread (Malaysian banking convention)
  // Banks price as "SBR + 2.50%" etc. Given an effective rate, derive the spread.
  effectiveToSBRSpread(effectiveRate, sbr = 0.03) {
    return Math.max(0, (effectiveRate || 0) - sbr);
  },

  // Backward-compat alias
  flatToEffective(principal, flatRate, years) {
    const flat = Calc.finance.flatRate(principal, flatRate, years);
    return Calc.finance.solveEffectiveRate(principal, flat.monthlyInstalment, years);
  },

  // ===== INSTALMENT HELPERS ==============================================

  creditCardMinimum(outstanding, percent = 0.05, floor = 50) {
    return Math.max(outstanding * percent, floor);
  },
  totalMonthly(debts) {
    return debts.reduce((sum, d) => sum + (Number(d.instalment) || 0), 0);
  },
  totalOutstanding(debts) {
    return debts.reduce((sum, d) => sum + (Number(d.outstanding) || 0), 0);
  },
  dsr(monthlyCommitments, monthlyIncome) {
    if (!monthlyIncome) return 0;
    return monthlyCommitments / monthlyIncome;
  },

  // ===== AMORTIZATION SCHEDULES ==========================================
  //
  // method = 'flat'      : FLAT-RATE with RULE OF 78 (Sum-of-Years' Digits)
  //                        Malaysian standard for HP (HPA 1967), Personal
  //                        Financing-i, conventional Personal Loan.
  //                        Monthly payment is CONSTANT.
  //                        Profit allocation: front-loaded — month 1 carries
  //                        the largest profit charge, month n the smallest.
  //                        This is what Malaysian banks print on their
  //                        repayment schedules and what governs early-
  //                        settlement (ibra' / rebate).
  //
  // method = 'reducing'  : Standard PMT amortization.
  //                        Used for mortgages and any reducing-balance loan.
  //                        Profit each month = current balance × monthly rate.
  //
  // Returns array of { month, instalment, principal, profit, balance }
  //
  amortizationSchedule(principal, annualRate, years, method = 'flat') {
    const months = (years || 0) * 12;
    const P = principal || 0;
    const R = annualRate || 0;
    const Y = years || 0;
    const rows = [];
    if (months <= 0 || P <= 0) return rows;

    if (method === 'flat') {
      // ---------- FLAT-RATE WITH RULE OF 78 ----------
      const totalProfit = P * R * Y;
      const totalPayment = P + totalProfit;
      const instalment = totalPayment / months;
      const sumOfDigits = months * (months + 1) / 2;

      let balance = P;
      let cumulativePrincipal = 0;
      for (let m = 1; m <= months; m++) {
        // Rule of 78: profit in month m proportional to (n - m + 1) / sumOfDigits
        const profit = totalProfit * (months - m + 1) / sumOfDigits;
        const principalPaid = instalment - profit;
        cumulativePrincipal += principalPaid;
        balance = m === months ? 0 : Math.max(0, P - cumulativePrincipal);
        rows.push({
          month: m,
          instalment,
          principal: principalPaid,
          profit,
          balance,
        });
      }
    } else {
      // ---------- REDUCING BALANCE (PMT) ----------
      const r = R / 12;
      const instalment = r === 0
        ? P / months
        : (P * r) / (1 - Math.pow(1 + r, -months));
      let balance = P;
      for (let m = 1; m <= months; m++) {
        const profit = balance * r;
        const principalPaid = instalment - profit;
        balance -= principalPaid;
        if (m === months) balance = 0;
        rows.push({
          month: m,
          instalment,
          principal: principalPaid,
          profit,
          balance: Math.max(0, balance),
        });
      }
    }

    return rows;
  },

  amortizationByYear(schedule, principal) {
    const byYear = [];
    let starting = principal;
    const totalYears = Math.ceil(schedule.length / 12);
    for (let y = 0; y < totalYears; y++) {
      const months = schedule.slice(y * 12, (y + 1) * 12);
      if (months.length === 0) break;
      const totalInstalment = months.reduce((s, r) => s + r.instalment, 0);
      const totalPrincipal = months.reduce((s, r) => s + r.principal, 0);
      const totalProfit = months.reduce((s, r) => s + r.profit, 0);
      const endingBalance = months[months.length - 1].balance;
      byYear.push({
        year: y + 1,
        startingBalance: starting,
        totalInstalment, totalPrincipal, totalProfit,
        endingBalance,
      });
      starting = endingBalance;
    }
    return byYear;
  },

  // ===== MALAYSIAN PROPERTY HELPERS ======================================

  stampDutySPA(price) {
    let p = price || 0, duty = 0;
    const tiers = [
      { upto: 100000,   rate: 0.01 },
      { upto: 500000,   rate: 0.02 },
      { upto: 1000000,  rate: 0.03 },
      { upto: Infinity, rate: 0.04 },
    ];
    let prev = 0;
    for (const t of tiers) {
      const slice = Math.max(0, Math.min(p, t.upto) - prev);
      duty += slice * t.rate;
      prev = t.upto;
      if (p <= t.upto) break;
    }
    return duty;
  },

  stampDutyLoan(loanAmount) {
    return (loanAmount || 0) * 0.005;
  },

  legalFees(amount) {
    let a = amount || 0, fee = 0;
    const tiers = [
      { upto: 500000,    rate: 0.0125 },
      { upto: 1000000,   rate: 0.01 },
      { upto: 3000000,   rate: 0.007 },
      { upto: 5000000,   rate: 0.006 },
      { upto: Infinity,  rate: 0.005 },
    ];
    let prev = 0;
    for (const t of tiers) {
      const slice = Math.max(0, Math.min(a, t.upto) - prev);
      fee += slice * t.rate;
      prev = t.upto;
      if (a <= t.upto) break;
    }
    return Math.max(500, fee);
  },

  // ===== SANITY CHECK / SELF-VERIFY ======================================
  //
  // Run in browser console: Calc.finance._verify()
  // Logs PASS/FAIL for known Malaysian banking test cases.
  //
  _verify() {
    const tests = [];
    const eq = (a, b, tol, label) => {
      const pass = Math.abs(a - b) <= tol;
      tests.push({ label, expected: b, actual: a, pass });
      console[pass ? 'log' : 'error'](
        (pass ? '✓' : '✗') + ' ' + label + ` — expected ${b.toFixed(4)}, got ${a.toFixed(4)}`
      );
    };

    // 1. Flat rate calculation
    const f1 = Calc.finance.flatRate(76000, 0.0268, 6);
    eq(f1.totalInterest, 12220.80, 0.01, 'Flat 76k @ 2.68%/6yr → totalInterest');
    eq(f1.monthlyInstalment, 1225.29, 0.01, 'Flat 76k @ 2.68%/6yr → monthly');

    // 2. Effective rate solver (PMT-IRR)
    const eff = Calc.finance.solveEffectiveRate(76000, 1225.29, 6);
    eq(eff, 0.0504, 0.001, 'Effective rate for monthly 1225.29 @ 6yr → ~5.04%');

    // 3. Reducing balance (PMT)
    const r1 = Calc.finance.reducingRate(100000, 0.055, 30);
    eq(r1.monthlyInstalment, 567.79, 0.05, 'Mortgage 100k @ 5.5%/30yr → 567.79/mo');

    // 4. Rule of 78 amortization
    const am = Calc.finance.amortizationSchedule(76000, 0.0268, 6, 'flat');
    eq(am.length, 72, 0, 'Schedule length = 72 months');
    eq(am[0].profit, 12220.80 * 72 / 2628, 0.01, 'Month 1 profit (Rule of 78)');
    eq(am[0].principal, 1225.29 - (12220.80 * 72 / 2628), 0.01, 'Month 1 principal');
    eq(am[71].profit, 12220.80 * 1 / 2628, 0.01, 'Month 72 profit (smallest)');
    const totalProfit = am.reduce((s, r) => s + r.profit, 0);
    eq(totalProfit, 12220.80, 0.01, 'Sum of all profit = total profit');
    const totalPrincipal = am.reduce((s, r) => s + r.principal, 0);
    eq(totalPrincipal, 76000, 0.01, 'Sum of all principal = original P');

    // 5. Stamp duty
    eq(Calc.finance.stampDutySPA(500000), 9000, 0.01, 'SPA stamp duty on RM500k');
    eq(Calc.finance.stampDutySPA(1000000), 24000, 0.01, 'SPA stamp duty on RM1M');

    const passed = tests.filter(t => t.pass).length;
    console.log(`\n=== ${passed}/${tests.length} tests passed ===`);
    return tests;
  },
};
